import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Tabs, { ActiveTabType, ITabMap, TabsRef } from "../Tabs";
import useActionsContent from "./useActionsContent";
import Content from "../Content";
import { INotification } from "Data/interfaces/Notification/INotification";
import { NOTIFICATIONS_PERSONAL_KEY } from "Data/keys/QueryKeys";

interface IActionsContent {
  tabsRef: React.RefObject<TabsRef>;
  constructionSiteId: number | null;
  loadingExternalLink: boolean;
  allCount: number;
  allCountObra: number;
  allCountPersonal: number;
  readingNotificationId: string | null;
  isLoadingReadNotification: boolean;
  onRedirect: (url: string) => void;
  onMarkAsRead: (readRow: INotification) => void;
  onTabChange: (tab: ActiveTabType) => void;
}

const ActionsContent: FC<IActionsContent> = ({
  tabsRef,
  constructionSiteId,
  loadingExternalLink,
  allCount,
  allCountObra,
  allCountPersonal,
  readingNotificationId,
  isLoadingReadNotification,
  onRedirect,
  onMarkAsRead,
  onTabChange,
}) => {
  const { t } = useTranslation('notification');
  useActionsContent({ tabsRef, constructionSiteId });

  const items = useMemo(() => {
    const opts: ITabMap = new Map();

    opts.set('personal', {
      id: 'personal',
      label: t('widget.tabs.personal'),
      isDisabled: false,
      tagNumber: constructionSiteId ? allCountPersonal : undefined,
      newTagNumber: !constructionSiteId ? allCountPersonal : undefined,
      children: (
        <Content
          personalKey={NOTIFICATIONS_PERSONAL_KEY}
          constructionSiteId={null}
          readingNotificationId={readingNotificationId}
          isLoadingReadNotification={isLoadingReadNotification}
          onRedirect={onRedirect}
          onMarkAsRead={onMarkAsRead}
        />
      ),
    });
    if (constructionSiteId) {
      opts.set('constructionSite', {
        id: 'constructionSite',
        label: t('widget.tabs.obra'),
        isDisabled: false,
        newTagNumber: allCountObra,
        children: (
          <Content
            constructionSiteId={constructionSiteId}
            personalKey={null}
            readingNotificationId={readingNotificationId}
            isLoadingReadNotification={isLoadingReadNotification}
            onRedirect={onRedirect}
            onMarkAsRead={onMarkAsRead}
          />
        ),
      });
    }
    opts.set('allConstructionSites', {
      id: 'allConstructionSites',
      label: t('widget.tabs.all'),
      isDisabled: false,
      tagNumber: constructionSiteId ? allCount : undefined,
      newTagNumber: !constructionSiteId ? allCount : undefined,
      children: (
        <Content
          constructionSiteId={null}
          personalKey={null}
          readingNotificationId={readingNotificationId}
          isLoadingReadNotification={isLoadingReadNotification}
          onRedirect={onRedirect}
          onMarkAsRead={onMarkAsRead}
        />
      ),
    });

    return opts;
  }, [
    constructionSiteId,
    allCount,
    allCountObra,
    allCountPersonal,
    readingNotificationId,
    isLoadingReadNotification,
    onRedirect,
    onMarkAsRead,
    t,
  ]);

  return (
    <Tabs
      ref={tabsRef}
      defaultActiveTab="allConstructionSites"
      options={items}
      loadingExternalLink={loadingExternalLink}
      onTabChange={onTabChange}
    />
  );
}

export default ActionsContent;
